import { useState, useEffect, useCallback } from 'react';
import { hospitalApi } from '../../hospitalApi';

const C = '#1b6ca8';
const DARK = '#0f4c75';
const LIGHT = '#e8f4fd';

const ROLE_META = {
  admin:  { label: 'Admin',   color: '#c0392b', bg: '#fce8e6', desc: 'Full access — create, edit, delete, manage users' },
  editor: { label: 'Editor',  color: '#e67e22', bg: '#fef9e7', desc: 'Create and edit records; cannot delete or manage users' },
  viewer: { label: 'Viewer',  color: '#27ae60', bg: '#e6f4ea', desc: 'Read-only — can view all data, cannot make changes' },
};

const EMPTY = { username: '', fullName: '', password: '', role: 'viewer' };

function RoleBadge({ role }) {
  const m = ROLE_META[role] || { label: role, color: '#888', bg: '#f0f0f0' };
  return (
    <span style={{ background: m.bg, color: m.color, borderRadius: 6,
      padding: '2px 9px', fontSize: 11, fontWeight: 700 }}>
      {m.label}
    </span>
  );
}

function SystemUsersTab() {
  const [users, setUsers]       = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [form, setForm]         = useState(EMPTY);
  const [pwdForm, setPwdForm]   = useState({ id: null, password: '', confirm: '' });
  const [showPwd, setShowPwd]   = useState(null);
  const [saving, setSaving]     = useState(false);
  const [err, setErr]           = useState('');
  const [ok, setOk]             = useState('');

  const load = useCallback(() => {
    hospitalApi.getSystemUsers().then(d => { if (Array.isArray(d)) setUsers(d); });
  }, []);

  useEffect(() => { load(); }, [load]);

  const flash = (msg) => { setOk(msg); setTimeout(() => setOk(''), 3000); };

  const startAdd = () => { setForm(EMPTY); setEditUser(null); setErr(''); setShowForm(true); };
  const startEdit = (u) => {
    setForm({ username: u.username, fullName: u.full_name || '', password: '', role: u.role });
    setEditUser(u);
    setErr('');
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.username.trim()) { setErr('Username is required'); return; }
    if (!editUser && form.password.length < 6) { setErr('Password must be at least 6 characters'); return; }
    setSaving(true); setErr('');
    try {
      if (editUser) {
        const res = await hospitalApi.updateSystemUser(editUser.id, {
          fullName: form.fullName, role: form.role, status: editUser.status || 'active'
        });
        if (res?.error) { setErr(res.error); setSaving(false); return; }
      } else {
        const res = await hospitalApi.createSystemUser({
          username: form.username.trim().toLowerCase(),
          fullName: form.fullName, password: form.password, role: form.role
        });
        if (res?.error) { setErr(res.error); setSaving(false); return; }
      }
      load();
      setShowForm(false);
      flash(editUser ? 'User updated' : 'User created successfully');
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  const handleToggleStatus = async (u) => {
    const newStatus = u.status === 'active' ? 'inactive' : 'active';
    await hospitalApi.updateSystemUser(u.id, { fullName: u.full_name || '', role: u.role, status: newStatus });
    load();
  };

  const handleDelete = async (u) => {
    if (!window.confirm(`Delete user "${u.username}"? This cannot be undone.`)) return;
    await hospitalApi.deleteSystemUser(u.id);
    load();
    flash('User deleted');
  };

  const handleResetPwd = async () => {
    if (pwdForm.password.length < 6) { setErr('Password must be at least 6 characters'); return; }
    if (pwdForm.password !== pwdForm.confirm) { setErr('Passwords do not match'); return; }
    setSaving(true); setErr('');
    try {
      const res = await hospitalApi.resetSystemUserPassword(showPwd, { password: pwdForm.password });
      if (res?.error) { setErr(res.error); setSaving(false); return; }
      setShowPwd(null);
      setPwdForm({ id: null, password: '', confirm: '' });
      flash('Password updated');
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  const inp = { className: 'form-control' };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: 16, color: DARK }}>System Users</div>
          <div style={{ fontSize: 12, color: '#9aa0a6', marginTop: 2 }}>
            Manage staff access to the hospital management system
          </div>
        </div>
        <button className="btn btn-primary" onClick={startAdd}>+ Add User</button>
      </div>

      {ok && (
        <div style={{ background: '#e6f4ea', color: '#27ae60', borderRadius: 8,
          padding: '10px 14px', marginBottom: 14, fontSize: 13, fontWeight: 600 }}>
          ✓ {ok}
        </div>
      )}

      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        {Object.entries(ROLE_META).map(([key, m]) => (
          <div key={key} style={{ background: '#fff', border: '1px solid #e8eaed',
            borderRadius: 8, padding: '8px 14px', display: 'flex', alignItems: 'flex-start', gap: 10, flex: '1 1 200px' }}>
            <RoleBadge role={key} />
            <div style={{ fontSize: 11, color: '#5f6368', lineHeight: 1.4 }}>{m.desc}</div>
          </div>
        ))}
      </div>

      {showForm && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card-header">
            <div className="card-title">{editUser ? `Edit: ${editUser.username}` : 'Add New User'}</div>
          </div>
          <div className="card-body">
            {err && (
              <div style={{ background: '#fce8e6', color: '#c5221f', borderRadius: 8,
                padding: '8px 12px', marginBottom: 14, fontSize: 13 }}>⚠ {err}</div>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
              <div className="form-group">
                <label className="form-label">Username *</label>
                <input {...inp} type="text" value={form.username}
                  disabled={!!editUser}
                  onChange={e => setForm(f => ({ ...f, username: e.target.value }))}
                  placeholder="e.g. dr.sharma" />
              </div>
              <div className="form-group">
                <label className="form-label">Full Name</label>
                <input {...inp} type="text" value={form.fullName}
                  onChange={e => setForm(f => ({ ...f, fullName: e.target.value }))}
                  placeholder="Dr. Amit Sharma" />
              </div>
              {!editUser && (
                <div className="form-group">
                  <label className="form-label">Password *</label>
                  <input {...inp} type="password" value={form.password}
                    onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                    placeholder="Minimum 6 characters" />
                </div>
              )}
              <div className="form-group">
                <label className="form-label">Access Role *</label>
                <select {...inp} value={form.role}
                  onChange={e => setForm(f => ({ ...f, role: e.target.value }))}>
                  <option value="viewer">Viewer — read only</option>
                  <option value="editor">Editor — create &amp; edit</option>
                  <option value="admin">Admin — full access</option>
                </select>
              </div>
            </div>
            <div style={{ marginTop: 16, display: 'flex', gap: 10 }}>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving…' : editUser ? '✓ Save Changes' : '+ Create User'}
              </button>
              <button style={{ padding: '8px 18px', borderRadius: 8, border: '1px solid #e8eaed',
                background: '#fff', cursor: 'pointer', fontSize: 13 }}
                onClick={() => { setShowForm(false); setErr(''); }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {showPwd && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.4)', zIndex: 2000,
          display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 14, padding: 28, width: '100%', maxWidth: 360,
            boxShadow: '0 20px 60px rgba(0,0,0,.3)' }}>
            <div style={{ fontWeight: 700, fontSize: 15, color: DARK, marginBottom: 16 }}>Reset Password</div>
            {err && (
              <div style={{ background: '#fce8e6', color: '#c5221f', borderRadius: 8,
                padding: '8px 12px', marginBottom: 12, fontSize: 13 }}>⚠ {err}</div>
            )}
            <div className="form-group">
              <label className="form-label">New Password</label>
              <input {...inp} type="password" value={pwdForm.password}
                onChange={e => setPwdForm(f => ({ ...f, password: e.target.value }))}
                placeholder="Minimum 6 characters" />
            </div>
            <div className="form-group">
              <label className="form-label">Confirm Password</label>
              <input {...inp} type="password" value={pwdForm.confirm}
                onChange={e => setPwdForm(f => ({ ...f, confirm: e.target.value }))} />
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
              <button className="btn btn-primary" onClick={handleResetPwd} disabled={saving}
                style={{ flex: 1 }}>
                {saving ? 'Saving…' : '🔑 Reset Password'}
              </button>
              <button style={{ padding: '8px 18px', borderRadius: 8, border: '1px solid #e8eaed',
                background: '#fff', cursor: 'pointer', fontSize: 13 }}
                onClick={() => { setShowPwd(null); setErr(''); setPwdForm({ id: null, password: '', confirm: '' }); }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {users.length === 0 ? (
        <div style={{ padding: 40, textAlign: 'center', color: '#9aa0a6',
          background: '#fff', borderRadius: 10, boxShadow: '0 1px 6px rgba(0,0,0,.08)' }}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>👤</div>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>No system users yet</div>
          <div style={{ fontSize: 13 }}>Click <strong>Add User</strong> to create staff accounts with access roles</div>
          <div style={{ fontSize: 12, marginTop: 8, color: '#bbb' }}>
            Staff log in at <code>/spring/login</code>
          </div>
        </div>
      ) : (
        <div className="card">
          <div className="card-body" style={{ padding: 0 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: LIGHT }}>
                  {['Username', 'Full Name', 'Role', 'Status', 'Created', 'Actions'].map(h => (
                    <th key={h} style={{ padding: '10px 14px', textAlign: 'left',
                      color: DARK, fontWeight: 700, fontSize: 12 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {users.map((u, i) => (
                  <tr key={u.id} style={{ borderTop: '1px solid #f1f3f4',
                    background: i % 2 === 0 ? '#fff' : '#fafbfc',
                    opacity: u.status !== 'active' ? 0.55 : 1 }}>
                    <td style={{ padding: '10px 14px', fontWeight: 600, color: DARK }}>
                      {u.username}
                      {u.status !== 'active' && (
                        <span style={{ fontSize: 10, marginLeft: 6, color: '#c5221f',
                          background: '#fce8e6', borderRadius: 4, padding: '1px 5px' }}>inactive</span>
                      )}
                    </td>
                    <td style={{ padding: '10px 14px', color: '#5f6368' }}>{u.full_name || '—'}</td>
                    <td style={{ padding: '10px 14px' }}><RoleBadge role={u.role} /></td>
                    <td style={{ padding: '10px 14px' }}>
                      <span style={{
                        background: u.status === 'active' ? '#e6f4ea' : '#f1f3f4',
                        color: u.status === 'active' ? '#27ae60' : '#9aa0a6',
                        borderRadius: 6, padding: '2px 8px', fontSize: 11, fontWeight: 700
                      }}>{u.status || 'active'}</span>
                    </td>
                    <td style={{ padding: '10px 14px', color: '#9aa0a6', fontSize: 12 }}>
                      {u.created_at ? u.created_at.toString().substring(0, 10) : '—'}
                    </td>
                    <td style={{ padding: '10px 14px' }}>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        <button onClick={() => startEdit(u)}
                          style={{ fontSize: 11, padding: '4px 10px', background: LIGHT,
                            color: DARK, border: `1px solid ${C}`, borderRadius: 5, cursor: 'pointer', fontWeight: 600 }}>
                          Edit
                        </button>
                        <button onClick={() => { setShowPwd(u.id); setErr(''); }}
                          style={{ fontSize: 11, padding: '4px 10px', background: '#fef9e7',
                            color: '#e67e22', border: '1px solid #f0c080', borderRadius: 5, cursor: 'pointer', fontWeight: 600 }}>
                          🔑 Pwd
                        </button>
                        <button onClick={() => handleToggleStatus(u)}
                          style={{ fontSize: 11, padding: '4px 10px',
                            background: u.status === 'active' ? '#fff3f0' : '#e6f4ea',
                            color: u.status === 'active' ? '#c5221f' : '#27ae60',
                            border: `1px solid ${u.status === 'active' ? '#f5c6c6' : '#b7dfb8'}`,
                            borderRadius: 5, cursor: 'pointer', fontWeight: 600 }}>
                          {u.status === 'active' ? 'Deactivate' : 'Activate'}
                        </button>
                        <button onClick={() => handleDelete(u)}
                          style={{ fontSize: 11, padding: '4px 10px', background: '#fce8e6',
                            color: '#c5221f', border: '1px solid #f5c6c6', borderRadius: 5, cursor: 'pointer', fontWeight: 600 }}>
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div style={{ marginTop: 16, background: '#fff', borderRadius: 10, padding: '14px 18px',
        border: '1px solid #e8eaed', fontSize: 12, color: '#5f6368' }}>
        <strong style={{ color: DARK }}>Staff login URL:</strong>{' '}
        <code style={{ background: '#f4f6f9', padding: '2px 6px', borderRadius: 4 }}>
          {window.location.origin}/spring/login
        </code>
        <span style={{ marginLeft: 8, color: '#9aa0a6' }}>
          Share this URL with hospital staff so they can log in with their assigned credentials.
        </span>
      </div>
    </div>
  );
}

const BASE = '/spring/hospital/api';

async function patientAccountsFetch(path, opts = {}) {
  const res = await fetch(BASE + path, {
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    ...opts,
  });
  return res.json().catch(() => ({}));
}

const GRANT_EMPTY = { patientId: '', username: '', password: '' };
const RESET_EMPTY = { password: '' };

function PatientAccountsTab() {
  const [accounts, setAccounts]       = useState([]);
  const [patients, setPatients]       = useState([]);
  const [loading, setLoading]         = useState(true);
  const [showGrant, setShowGrant]     = useState(false);
  const [grantForm, setGrantForm]     = useState(GRANT_EMPTY);
  const [patientSearch, setPatientSearch] = useState('');
  const [showReset, setShowReset]     = useState(null);
  const [resetForm, setResetForm]     = useState(RESET_EMPTY);
  const [saving, setSaving]           = useState(false);
  const [err, setErr]                 = useState('');
  const [ok, setOk]                   = useState('');

  const loadAccounts = useCallback(() => {
    setLoading(true);
    patientAccountsFetch('/patient-accounts')
      .then(d => { if (Array.isArray(d)) setAccounts(d); })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadAccounts(); }, [loadAccounts]);

  const loadPatients = () => {
    patientAccountsFetch('/patients').then(d => { if (Array.isArray(d)) setPatients(d); });
  };

  const flash = (msg) => { setOk(msg); setTimeout(() => setOk(''), 3000); };

  const openGrant = () => {
    setGrantForm(GRANT_EMPTY);
    setPatientSearch('');
    setErr('');
    setShowGrant(true);
    loadPatients();
  };

  const filteredPatients = patients.filter(p =>
    !patientSearch || (p.name || '').toLowerCase().includes(patientSearch.toLowerCase())
  );

  const selectPatient = (p) => {
    setGrantForm(f => ({ ...f, patientId: p.id, username: p.patient_code || '' }));
    setPatientSearch(p.name || '');
  };

  const handleGrant = async () => {
    if (!grantForm.patientId) { setErr('Select a patient'); return; }
    if (!grantForm.username.trim()) { setErr('Username is required'); return; }
    if (grantForm.password.length < 6) { setErr('Password must be at least 6 characters'); return; }
    setSaving(true); setErr('');
    try {
      const res = await patientAccountsFetch(`/patient-accounts/${grantForm.patientId}`, {
        method: 'POST',
        body: JSON.stringify({ username: grantForm.username.trim(), password: grantForm.password }),
      });
      if (res?.error) { setErr(res.error); setSaving(false); return; }
      loadAccounts();
      setShowGrant(false);
      flash('Portal access granted');
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  const handleResetPassword = async () => {
    if (resetForm.password.length < 6) { setErr('Password must be at least 6 characters'); return; }
    setSaving(true); setErr('');
    try {
      const res = await patientAccountsFetch(`/patient-accounts/${showReset}/password`, {
        method: 'PUT',
        body: JSON.stringify({ password: resetForm.password }),
      });
      if (res?.error) { setErr(res.error); setSaving(false); return; }
      setShowReset(null);
      setResetForm(RESET_EMPTY);
      flash('Password reset successfully');
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  const handleRevoke = async (acct) => {
    if (!window.confirm(`Revoke portal access for "${acct.patient_name}"? They will no longer be able to log in.`)) return;
    await patientAccountsFetch(`/patient-accounts/${acct.patient_id}`, { method: 'DELETE' });
    loadAccounts();
    flash('Access revoked');
  };

  const handleToggleStatus = async (acct) => {
    await patientAccountsFetch(`/patient-accounts/${acct.patient_id}/status`, {
      method: 'PUT',
      body: JSON.stringify({ isActive: !acct.is_active }),
    });
    loadAccounts();
  };

  const inp = { className: 'form-control' };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: 16, color: DARK }}>Patient Accounts</div>
          <div style={{ fontSize: 12, color: '#9aa0a6', marginTop: 2 }}>
            Manage patient portal login access
          </div>
        </div>
        <button className="btn btn-primary" onClick={openGrant}>+ Grant Access</button>
      </div>

      <div style={{ marginBottom: 16, background: LIGHT, borderRadius: 9, padding: '12px 16px',
        border: `1px solid ${C}20`, fontSize: 13, color: '#3c4043' }}>
        <strong style={{ color: DARK }}>Patient portal login URL:</strong>{' '}
        <code style={{ background: '#fff', padding: '2px 8px', borderRadius: 5, userSelect: 'all' }}>
          {window.location.origin}/spring/login
        </code>
        <span style={{ marginLeft: 8, color: '#5f6368', fontSize: 12 }}>
          Share this URL and the patient's credentials with them so they can access their records.
        </span>
      </div>

      {ok && (
        <div style={{ background: '#e6f4ea', color: '#27ae60', borderRadius: 8,
          padding: '10px 14px', marginBottom: 14, fontSize: 13, fontWeight: 600 }}>
          ✓ {ok}
        </div>
      )}

      {showGrant && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.4)', zIndex: 2000,
          display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 14, padding: 28, width: '100%', maxWidth: 420,
            boxShadow: '0 20px 60px rgba(0,0,0,.3)' }}>
            <div style={{ fontWeight: 700, fontSize: 15, color: DARK, marginBottom: 16 }}>Grant Portal Access</div>
            {err && (
              <div style={{ background: '#fce8e6', color: '#c5221f', borderRadius: 8,
                padding: '8px 12px', marginBottom: 12, fontSize: 13 }}>⚠ {err}</div>
            )}
            <div className="form-group" style={{ position: 'relative' }}>
              <label className="form-label">Select Patient *</label>
              <input {...inp} type="text" value={patientSearch}
                onChange={e => { setPatientSearch(e.target.value); setGrantForm(f => ({ ...f, patientId: '', username: '' })); }}
                placeholder="Search by patient name..." />
              {patientSearch && !grantForm.patientId && filteredPatients.length > 0 && (
                <div style={{ position: 'absolute', zIndex: 10, left: 0, right: 0,
                  background: '#fff', border: '1px solid #e8eaed', borderRadius: 8,
                  boxShadow: '0 8px 24px rgba(0,0,0,.12)', maxHeight: 200, overflowY: 'auto' }}>
                  {filteredPatients.slice(0, 20).map(p => (
                    <div key={p.id}
                      onClick={() => selectPatient(p)}
                      style={{ padding: '9px 14px', cursor: 'pointer', fontSize: 13,
                        borderBottom: '1px solid #f1f3f4' }}
                      onMouseEnter={e => e.currentTarget.style.background = LIGHT}
                      onMouseLeave={e => e.currentTarget.style.background = '#fff'}>
                      <span style={{ fontWeight: 600 }}>{p.name}</span>
                      {p.patient_code && <span style={{ color: '#9aa0a6', marginLeft: 8, fontSize: 12 }}>{p.patient_code}</span>}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="form-group">
              <label className="form-label">Portal Username *</label>
              <input {...inp} type="text" value={grantForm.username}
                onChange={e => setGrantForm(f => ({ ...f, username: e.target.value }))}
                placeholder="e.g. patient_code or email" />
            </div>
            <div className="form-group">
              <label className="form-label">Password *</label>
              <input {...inp} type="password" value={grantForm.password}
                onChange={e => setGrantForm(f => ({ ...f, password: e.target.value }))}
                placeholder="Minimum 6 characters" />
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
              <button className="btn btn-primary" onClick={handleGrant} disabled={saving} style={{ flex: 1 }}>
                {saving ? 'Saving…' : 'Grant Access'}
              </button>
              <button style={{ padding: '8px 18px', borderRadius: 8, border: '1px solid #e8eaed',
                background: '#fff', cursor: 'pointer', fontSize: 13 }}
                onClick={() => { setShowGrant(false); setErr(''); }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {showReset && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.4)', zIndex: 2000,
          display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 14, padding: 28, width: '100%', maxWidth: 360,
            boxShadow: '0 20px 60px rgba(0,0,0,.3)' }}>
            <div style={{ fontWeight: 700, fontSize: 15, color: DARK, marginBottom: 16 }}>Reset Patient Password</div>
            {err && (
              <div style={{ background: '#fce8e6', color: '#c5221f', borderRadius: 8,
                padding: '8px 12px', marginBottom: 12, fontSize: 13 }}>⚠ {err}</div>
            )}
            <div className="form-group">
              <label className="form-label">New Password</label>
              <input {...inp} type="password" value={resetForm.password}
                onChange={e => setResetForm(f => ({ ...f, password: e.target.value }))}
                placeholder="Minimum 6 characters" />
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
              <button className="btn btn-primary" onClick={handleResetPassword} disabled={saving} style={{ flex: 1 }}>
                {saving ? 'Saving…' : '🔑 Reset Password'}
              </button>
              <button style={{ padding: '8px 18px', borderRadius: 8, border: '1px solid #e8eaed',
                background: '#fff', cursor: 'pointer', fontSize: 13 }}
                onClick={() => { setShowReset(null); setErr(''); setResetForm(RESET_EMPTY); }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: '#9aa0a6' }}>Loading…</div>
      ) : accounts.length === 0 ? (
        <div style={{ padding: 40, textAlign: 'center', color: '#9aa0a6',
          background: '#fff', borderRadius: 10, boxShadow: '0 1px 6px rgba(0,0,0,.08)' }}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>🔒</div>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>No patient accounts yet</div>
          <div style={{ fontSize: 13 }}>Click <strong>+ Grant Access</strong> to give a patient portal login credentials</div>
        </div>
      ) : (
        <div className="card">
          <div className="card-body" style={{ padding: 0 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: LIGHT }}>
                  {['Patient Name', 'Patient Code', 'Portal Username', 'Status', 'Actions'].map(h => (
                    <th key={h} style={{ padding: '10px 14px', textAlign: 'left',
                      color: DARK, fontWeight: 700, fontSize: 12 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {accounts.map((acct, i) => (
                  <tr key={acct.id || acct.patient_id} style={{ borderTop: '1px solid #f1f3f4',
                    background: i % 2 === 0 ? '#fff' : '#fafbfc' }}>
                    <td style={{ padding: '10px 14px', fontWeight: 600, color: DARK }}>
                      {acct.patient_name || '—'}
                    </td>
                    <td style={{ padding: '10px 14px', color: '#5f6368', fontFamily: 'monospace', fontSize: 12 }}>
                      {acct.patient_code || '—'}
                    </td>
                    <td style={{ padding: '10px 14px', color: '#5f6368' }}>
                      {acct.username || '—'}
                    </td>
                    <td style={{ padding: '10px 14px' }}>
                      <span style={{
                        background: acct.is_active ? '#e6f4ea' : '#f1f3f4',
                        color: acct.is_active ? '#27ae60' : '#9aa0a6',
                        borderRadius: 6, padding: '2px 8px', fontSize: 11, fontWeight: 700
                      }}>{acct.is_active ? 'Active' : 'Inactive'}</span>
                    </td>
                    <td style={{ padding: '10px 14px' }}>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        <button onClick={() => { setShowReset(acct.patient_id); setErr(''); setResetForm(RESET_EMPTY); }}
                          style={{ fontSize: 11, padding: '4px 10px', background: '#fef9e7',
                            color: '#e67e22', border: '1px solid #f0c080', borderRadius: 5, cursor: 'pointer', fontWeight: 600 }}>
                          🔑 Reset Pwd
                        </button>
                        <button onClick={() => handleToggleStatus(acct)}
                          style={{ fontSize: 11, padding: '4px 10px',
                            background: acct.is_active ? '#fff3f0' : '#e6f4ea',
                            color: acct.is_active ? '#c5221f' : '#27ae60',
                            border: `1px solid ${acct.is_active ? '#f5c6c6' : '#b7dfb8'}`,
                            borderRadius: 5, cursor: 'pointer', fontWeight: 600 }}>
                          {acct.is_active ? 'Deactivate' : 'Activate'}
                        </button>
                        <button onClick={() => handleRevoke(acct)}
                          style={{ fontSize: 11, padding: '4px 10px', background: '#fce8e6',
                            color: '#c5221f', border: '1px solid #f5c6c6', borderRadius: 5, cursor: 'pointer', fontWeight: 600 }}>
                          Revoke
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export default function HospitalUsers() {
  const [mainTab, setMainTab] = useState(0);

  const tabStyle = (active) => ({
    padding: '8px 20px', borderRadius: 20, border: 'none', cursor: 'pointer',
    fontSize: 13, fontWeight: active ? 700 : 500,
    background: active ? C : 'transparent',
    color: active ? '#fff' : '#5f6368',
    transition: 'all .15s',
  });

  return (
    <div>
      <div style={{ display: 'flex', gap: 6, marginBottom: 22,
        background: '#f4f6f9', borderRadius: 24, padding: 4, width: 'fit-content' }}>
        <button style={tabStyle(mainTab === 0)} onClick={() => setMainTab(0)}>System Users</button>
        <button style={tabStyle(mainTab === 1)} onClick={() => setMainTab(1)}>Patient Accounts</button>
      </div>
      {mainTab === 0 ? <SystemUsersTab /> : <PatientAccountsTab />}
    </div>
  );
}
